// src/api/settings.js
/**
 * @file api/settings.js
 * @description Settings & User Profile API Layer
 * 
 * Handles all BYOK and user profile related API calls:
 * - User profile retrieval
 * - API key management (save/delete/test)
 * - BYOK preference updates
 */

import { SETTINGS_API_URL } from '../config';
import { getAuthHeaders, handleAuthError } from './auth';

/**
 * Fetches the user's complete profile including credits and BYOK status.
 * Called on app load and when navigating to settings.
 * 
 * Backend: GET /api/user/settings
 * 
 * @returns {Promise<object>} User profile object
 * @throws {Error} If authentication fails or API error occurs
 */
export const getUserProfile = async () => {
    console.log('SETTINGS API: Fetching user profile...');
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(SETTINGS_API_URL, {
            method: 'GET',
            headers
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'profile fetch');
        }

        if (!response.ok) {
            throw new Error(`Settings API responded with status: ${response.status}`);
        }

        const data = await response.json();
        console.log('SETTINGS API: Profile fetched successfully');
        return data;
        
    } catch (error) {
        console.error('SETTINGS API: Failed to fetch profile:', error);
        throw error;
    }
};

/**
 * Saves an encrypted API key for a specific provider.
 * 
 * Backend: POST /api/user/settings/keys
 * 
 * @param {string} provider - Provider name ('openai', 'anthropic', 'google')
 * @param {string} apiKey - The plaintext API key to encrypt and store
 * @returns {Promise<object>} Success response
 * @throws {Error} If validation fails or API error occurs
 */
export const saveAPIKey = async (provider, apiKey) => {
    console.log(`SETTINGS API: Saving ${provider} API key...`);
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${SETTINGS_API_URL}/keys`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ provider, apiKey })
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'API key save');
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Failed to save ${provider} key`);
        }

        const data = await response.json();
        console.log(`SETTINGS API: ${provider} key saved successfully`);
        return data;
        
    } catch (error) {
        console.error(`SETTINGS API: Failed to save ${provider} key:`, error);
        throw error;
    }
};

/**
 * Updates user preferences (primarily the BYOK toggle).
 * 
 * Backend: PUT /api/user/settings/preferences
 * 
 * @param {boolean} useOwnKey - Whether to use BYOK or app credits
 * @returns {Promise<object>} Success response
 * @throws {Error} If API error occurs
 */
export const updateBYOKPreference = async (useOwnKey) => {
    console.log(`SETTINGS API: Updating BYOK preference to ${useOwnKey}...`);
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${SETTINGS_API_URL}/preferences`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ useOwnKey })
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'preference update');
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update preferences');
        }

        const data = await response.json();
        console.log('SETTINGS API: BYOK preference updated successfully');
        return data;
        
    } catch (error) {
        console.error('SETTINGS API: Failed to update preference:', error);
        throw error;
    }
};

/**
 * Deletes a stored API key for a specific provider.
 * 
 * Backend: DELETE /api/user/settings/keys/{provider}
 * 
 * @param {string} provider - Provider name ('openai', 'anthropic', 'google')
 * @returns {Promise<object>} Success response
 * @throws {Error} If API error occurs
 */
export const deleteAPIKey = async (provider) => {
    console.log(`SETTINGS API: Deleting ${provider} API key...`);
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${SETTINGS_API_URL}/keys/${provider}`, {
            method: 'DELETE',
            headers
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'API key deletion');
        }

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Failed to delete ${provider} key`);
        }

        const data = await response.json();
        console.log(`SETTINGS API: ${provider} key deleted successfully`);
        return data;
        
    } catch (error) {
        console.error(`SETTINGS API: Failed to delete ${provider} key:`, error);
        throw error;
    }
};

/**
 * Tests if a stored API key is valid by making a minimal API call.
 * 
 * Backend: POST /api/user/settings/keys/test
 * 
 * @param {string} provider - Provider name ('openai', 'anthropic', 'google')
 * @returns {Promise<object>} Test result with 'valid' boolean
 * @throws {Error} If API error occurs
 */
export const testAPIKey = async (provider) => {
    console.log(`SETTINGS API: Testing ${provider} API key...`);
    
    try {
        const headers = await getAuthHeaders();
        
        const response = await fetch(`${SETTINGS_API_URL}/keys/test`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ provider })
        });

        if (response.status === 401 || response.status === 403) {
            handleAuthError(response.status, 'API key test');
        }

        const data = await response.json();
        
        if (response.status === 404) {
            console.log(`SETTINGS API: No ${provider} key configured`);
            return { valid: false, error: 'No API key configured' };
        }

        if (!response.ok) {
            console.log(`SETTINGS API: ${provider} key is invalid`);
            return data; // Contains 'valid: false' and error message
        }

        console.log(`SETTINGS API: ${provider} key is valid`);
        return data;
        
    } catch (error) {
        console.error(`SETTINGS API: Failed to test ${provider} key:`, error);
        throw error;
    }
};